import { useState, useEffect, useCallback, useRef } from "react";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface UseAutoSaveOptions {
  onSave: () => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
}

export function useAutoSave({ onSave, debounceMs = 1500, enabled = true }: UseAutoSaveOptions) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);
  
  // Keep onSave in a ref to avoid dependency chain issues
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const markDirty = useCallback(() => {
    console.log("[useAutoSave] markDirty called, isInitialMount:", isInitialMount.current);
    if (isInitialMount.current) return;
    setIsDirty(true);
    // Don't reset saved status - let it show until next save cycle
  }, []);

  const markClean = useCallback(() => {
    console.log("[useAutoSave] markClean called - enabling auto-save");
    isInitialMount.current = false;
  }, []);

  const save = useCallback(async () => {
    console.log("[useAutoSave] save() called, enabled:", enabled);
    if (!enabled) return;
    
    setStatus("saving");
    setIsDirty(false); // Clear dirty immediately to prevent re-triggers
    
    try {
      console.log("[useAutoSave] Starting save...");
      await onSaveRef.current(); // Use ref to get latest onSave without dependency
      console.log("[useAutoSave] Save completed successfully");
      setStatus("saved");
      setLastSavedAt(new Date());
      
      // Clear any existing saved timeout
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
      
      // Reset to idle after showing "saved" for 2 seconds
      savedTimeoutRef.current = setTimeout(() => {
        setStatus("idle");
      }, 2000);
    } catch (error) {
      console.error("[useAutoSave] Save failed:", error);
      setStatus("error");
    }
  }, [enabled]); // Only depends on enabled - onSave accessed via ref

  // Auto-save when dirty
  useEffect(() => {
    console.log("[useAutoSave] Effect triggered - isDirty:", isDirty, "enabled:", enabled);
    if (!isDirty || !enabled) return;

    // Clear any existing save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    console.log("[useAutoSave] Scheduling save in", debounceMs, "ms");
    // Set new save timeout
    saveTimeoutRef.current = setTimeout(() => {
      console.log("[useAutoSave] Debounce complete, calling save()");
      save();
    }, debounceMs);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isDirty, save, debounceMs, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  return {
    status,
    isDirty,
    lastSavedAt,
    markDirty,
    markClean,
    save,
  };
}
